import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { de as deTranslations } from "@/locales/de";
import { en as enTranslations } from "@/locales/en";
import { translationService } from "@/services/translation.service";

export type Language = "de" | "en";

interface Translations {
  [key: string]: any;
}

export interface LocalizationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  translations: Record<Language, Translations>;
  updateTranslations: (lang: Language, translations: Translations) => void;
  availableLanguages: Language[];
  addLanguage: (lang: Language, translations: Translations) => void;
  reloadTranslations: (options?: {
    force?: boolean;
    languages?: Language[];
  }) => Promise<void>;
}

const LANGUAGE_STORAGE_KEY = "language";
const TRANSLATION_REFRESH_INTERVAL_MS = 120_000;

const LocalizationContext = createContext<LocalizationContextType | undefined>(
  undefined
);

const baseTranslations: Record<Language, Translations> = {
  de: deTranslations,
  en: enTranslations,
};

// Helper function to get nested translation by key (e.g., "header.title")
const getNestedTranslation = (obj: any, path: string): string => {
  const keys = path.split(".");
  let result = obj;

  for (const key of keys) {
    if (result && typeof result === "object" && key in result) {
      result = result[key];
    } else {
      return path; // Return the key itself if translation not found
    }
  }

  return typeof result === "string" ? result : path;
};

const cloneTranslations = (input: Translations): Translations =>
  JSON.parse(JSON.stringify(input));

const applyOverrides = (
  target: Translations,
  overrides: Record<string, string>
) => {
  Object.entries(overrides).forEach(([key, value]) => {
    const parts = key.split(".");
    let obj: any = target;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!obj[parts[i]] || typeof obj[parts[i]] !== "object") {
        obj[parts[i]] = {};
      }
      obj = obj[parts[i]];
    }
    obj[parts[parts.length - 1]] = value;
  });
};

const loadStoredLanguage = (): Language => {
  if (typeof window === "undefined") return "de";
  const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return saved === "de" || saved === "en" ? saved : "de";
};

export function LocalizationProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() =>
    loadStoredLanguage()
  );
  const [translations, setTranslations] = useState<
    Record<Language, Translations>
  >(() => ({
    de: cloneTranslations(baseTranslations.de),
    en: cloneTranslations(baseTranslations.en),
  }));

  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([
    "de",
    "en",
  ]);
  const initializedRef = useRef(false);

  const t = useCallback(
    (key: string): string => {
      const currentTranslations = translations[language] || translations["de"];
      return getNestedTranslation(currentTranslations, key);
    },
    [language, translations]
  );


  const fetchOverrides = useCallback(async (options?: {
    force?: boolean;
    languages?: Language[];
  }) => {
    const force = options?.force === true;
    const languagesToLoad = options?.languages ?? availableLanguages;
    const updates: Partial<Record<Language, Translations>> = {};
    await Promise.all(
      languagesToLoad.map(async (lang) => {
        try {
          const overrides = await translationService.getOverrides(lang, {
            force,
          });
          const nextTranslations = cloneTranslations(baseTranslations[lang]);
          applyOverrides(nextTranslations, overrides);
          updates[lang] = nextTranslations;
        } catch (error) {
          updates[lang] = cloneTranslations(baseTranslations[lang]);
        }
      })
    );
    return updates;
  }, [availableLanguages]);

  const reloadTranslations = useCallback(async (options?: {
    force?: boolean;
    languages?: Language[];
  }) => {
    const updates = await fetchOverrides(options);
    setTranslations((prev) => ({
      ...prev,
      ...updates,
    }));
  }, [fetchOverrides]);

  // Save language preference
  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }, [language]);

  useEffect(() => {
    let cancelled = false;

    const loadOverrides = async () => {
      const updates = await fetchOverrides();
      if (!cancelled) {
        setTranslations((prev) => ({
          ...prev,
          ...updates,
        }));
      }
    };

    void loadOverrides();
    initializedRef.current = true;

    return () => {
      cancelled = true;
    };
  }, [fetchOverrides]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updatedEventName = translationService.getOverridesUpdatedEventName();
    const updatedStorageKey = translationService.getOverridesUpdatedStorageKey();

    const handleUpdated = () => {
      void reloadTranslations({ force: true });
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== updatedStorageKey || !event.newValue) {
        return;
      }
      void reloadTranslations({ force: true });
    };

    const handleVisibleOrFocus = () => {
      if (document.visibilityState === "hidden") {
        return;
      }
      if (!initializedRef.current) {
        return;
      }
      void reloadTranslations({ force: true });
    };

    window.addEventListener(updatedEventName, handleUpdated as EventListener);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleVisibleOrFocus);
    document.addEventListener("visibilitychange", handleVisibleOrFocus);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "hidden") {
        return;
      }
      void reloadTranslations({ force: true });
    }, TRANSLATION_REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener(
        updatedEventName,
        handleUpdated as EventListener
      );
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleVisibleOrFocus);
      document.removeEventListener("visibilitychange", handleVisibleOrFocus);
      window.clearInterval(intervalId);
    };
  }, [reloadTranslations]);

  const updateTranslations = (
    lang: Language,
    newTranslations: Translations
  ) => {
    setTranslations((prev) => ({
      ...prev,
      [lang]: newTranslations,
    }));
    // In a real app, this would save to a database
    localStorage.setItem(
      `translations-${lang}`,
      JSON.stringify(newTranslations)
    );
  };

  const addLanguage = (lang: Language, newTranslations: Translations) => {
    setTranslations((prev) => ({
      ...prev,
      [lang]: newTranslations,
    }));
    setAvailableLanguages((prev) => [...new Set([...prev, lang])]);
    localStorage.setItem(
      `translations-${lang}`,
      JSON.stringify(newTranslations)
    );
  };

  return (
    <LocalizationContext.Provider
      value={{
        language,
        setLanguage,
        t,
        translations,
        updateTranslations,
        availableLanguages,
        addLanguage,
        reloadTranslations,
      }}
    >
      {children}
    </LocalizationContext.Provider>
  );
}

export function useLocalization() {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error("useLocalization must be used within LocalizationProvider");
  }
  return context;
}
